import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getErrorMessage } from '../utils/errorHandler';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Plus,
  Truck,
  Calendar,
  Users,
  Package,
  Sparkles,
  Loader2,
  ArrowLeft,
  Check,
  X,
  Minus,
  Edit,
  Trash2,
  PenLine,
  FileDown
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import SignaturePad from '../components/SignaturePad';

// List Page Component
export const DistribuzioniPage = () => {
  const { API, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [distribuzioni, setDistribuzioni] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDistribuzioni();
  }, []);

  const fetchDistribuzioni = async () => {
    try {
      const response = await axios.get(`${API}/distribuzioni`);
      setDistribuzioni(response.data);
    } catch (err) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const getTotaleValore = () => {
    return distribuzioni.reduce((sum, d) => sum + (d.valore_totale || 0), 0);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questa distribuzione? I prodotti torneranno in magazzino.')) return;
    try {
      await axios.delete(`${API}/distribuzioni/${id}`);
      toast.success('Distribuzione eliminata e magazzino ripristinato');
      fetchDistribuzioni();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Errore durante l\'eliminazione'));
    }
  };

  const handleDownloadRicevuta = async (id) => {
    try {
      const response = await axios.get(`${API}/pdf/ricevuta-distribuzione/${id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ricevuta_${id.substring(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Ricevuta scaricata');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Errore nel generare la ricevuta'));
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6 animate-fade-in" data-testid="distribuzioni-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-heading font-bold">Distribuzioni</h1>
          <p className="text-xs sm:text-sm text-muted-foreground">Registra e visualizza le distribuzioni alle famiglie</p>
        </div>
        <Button onClick={() => navigate('/distribuzioni/nuova')} className="w-full sm:w-auto" data-testid="new-distribution-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nuova Distribuzione
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-6">
        <Card>
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Truck className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
              </div>
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-muted-foreground">Totale</p>
                <p className="text-xl sm:text-2xl font-heading font-bold">{distribuzioni.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-green-600/10 flex items-center justify-center flex-shrink-0">
                <Package className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
              </div>
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-muted-foreground">Valore</p>
                <p className="text-xl sm:text-2xl font-heading font-bold">{getTotaleValore().toFixed(0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="col-span-2 sm:col-span-1">
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                <Users className="w-5 h-5 sm:w-6 sm:h-6 text-amber-500" />
              </div>
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-muted-foreground">Persone</p>
                <p className="text-xl sm:text-2xl font-heading font-bold">
                  {distribuzioni.reduce((sum, d) => sum + (d.numero_componenti || 0), 0)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card>
        <CardHeader className="px-4 sm:px-6 pt-4 sm:pt-6 pb-2">
          <CardTitle className="text-sm sm:text-base">Storico Distribuzioni</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 sm:p-6 space-y-3">
              {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}
            </div>
          ) : distribuzioni.length === 0 ? (
            <div className="p-8 sm:p-12 text-center">
              <Truck className="w-10 h-10 sm:w-12 sm:h-12 mx-auto text-muted-foreground/50" />
              <p className="mt-3 text-sm text-muted-foreground">Nessuna distribuzione registrata</p>
              <Button className="mt-3" size="sm" onClick={() => navigate('/distribuzioni/nuova')}>
                <Plus className="w-4 h-4 mr-1" />
                Crea la prima
              </Button>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data</TableHead>
                      <TableHead>Famiglia</TableHead>
                      <TableHead className="text-center">Componenti</TableHead>
                      <TableHead>Prodotti</TableHead>
                      <TableHead>Operatore</TableHead>
                      <TableHead className="text-center">Firma</TableHead>
                      <TableHead className="text-right">Valore</TableHead>
                      <TableHead className="text-right">Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {distribuzioni.map((d) => (
                      <TableRow key={d.id} data-testid={`distribuzione-row-${d.id}`}>
                        <TableCell>
                          <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-muted-foreground" />{new Date(d.data).toLocaleDateString('it-IT')}</div>
                        </TableCell>
                        <TableCell><div className="font-medium">{d.famiglia_nome}</div></TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-1"><Users className="w-4 h-4 text-muted-foreground" />{d.numero_componenti}</div>
                        </TableCell>
                        <TableCell><Badge variant="secondary">{d.prodotti?.length || 0} prodotti</Badge></TableCell>
                        <TableCell>{d.operatore_nome}</TableCell>
                        <TableCell className="text-center">
                          {d.firma_ritiro ? (
                            <Badge variant="outline" className="text-green-600 border-green-600 text-[10px]"><PenLine className="w-3 h-3 mr-1" />Firmato</Badge>
                          ) : (
                            <Badge variant="outline" className="text-muted-foreground text-[10px]">No</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-medium">{(d.valore_totale || 0).toFixed(2)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            {d.firma_ritiro && (
                              <Button variant="ghost" size="icon" onClick={() => handleDownloadRicevuta(d.id)} title="Scarica ricevuta" data-testid={`download-ricevuta-${d.id}`}>
                                <FileDown className="w-4 h-4 text-green-600" />
                              </Button>
                            )}
                            {isAdmin() && (
                              <>
                                <Button variant="ghost" size="icon" onClick={() => navigate(`/distribuzioni/modifica/${d.id}`)} data-testid={`edit-dist-${d.id}`}>
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDelete(d.id)} data-testid={`delete-dist-${d.id}`}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {/* Mobile Card List */}
              <div className="md:hidden">
                {distribuzioni.map((d) => (
                  <div key={d.id} className="mobile-card-item" data-testid={`distribuzione-card-${d.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm">{d.famiglia_nome}</span>
                          <Badge variant="secondary" className="text-[10px] px-1.5 py-0">{d.prodotti?.length || 0} prod.</Badge>
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(d.data).toLocaleDateString('it-IT')}</span>
                          <span className="flex items-center gap-1"><Users className="w-3 h-3" />{d.numero_componenti}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <span className="font-medium text-sm">{(d.valore_totale || 0).toFixed(2)}</span>
                        {isAdmin() && (
                          <>
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate(`/distribuzioni/modifica/${d.id}`)}>
                              <Edit className="w-3.5 h-3.5" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDelete(d.id)}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// New Distribution Page Component
export const NuovaDistribuzionePage = () => {
  const { API, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedFamiglia = searchParams.get('famiglia');

  const [famiglie, setFamiglie] = useState([]);
  const [prodotti, setProdotti] = useState([]);
  const [selectedFamiglia, setSelectedFamiglia] = useState(null);
  const [suggerimenti, setSuggerimenti] = useState([]);
  const [carrello, setCarrello] = useState([]);
  const [note, setNote] = useState('');
  const [firma, setFirma] = useState(null);
  const [frequenza, setFrequenza] = useState('ogni_15_giorni');
  const [aiResult, setAiResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (preselectedFamiglia && famiglie.length > 0) {
      const famiglia = famiglie.find(f => f.id === preselectedFamiglia);
      if (famiglia) {
        setSelectedFamiglia(famiglia);
        fetchSuggestions(famiglia.id);
      }
    }
  }, [preselectedFamiglia, famiglie]);

  const fetchData = async () => {
    try {
      const [famiglieRes, prodottiRes] = await Promise.all([
        axios.get(`${API}/famiglie`),
        axios.get(`${API}/prodotti`)
      ]);
      setFamiglie(famiglieRes.data);
      setProdotti(prodottiRes.data.filter(p => p.quantita_disponibile > 0));
    } catch (err) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const fetchSuggestions = async (famigliaId, freq) => {
    setLoadingSuggestions(true);
    try {
      const response = await axios.post(`${API}/distribuzioni/ai-suggerimento`, {
        famiglia_id: famigliaId,
        frequenza: freq || frequenza
      });
      setAiResult(response.data);
      const sugg = response.data.prodotti || response.data.suggerimenti || [];
      setSuggerimenti(sugg);
      
      // Pre-fill cart with AI suggestions
      const cart = sugg.map(s => ({
        prodotto_id: s.prodotto_id,
        nome_prodotto: s.nome || s.nome_prodotto,
        quantita: s.quantita || s.quantita_suggerita || 1,
        max_quantita: prodotti.find(p => p.id === s.prodotto_id)?.quantita_disponibile || 99,
        motivo: s.motivo || ''
      }));
      setCarrello(cart);
    } catch (err) {
      toast.error('Errore nel calcolo AI. Puoi aggiungere prodotti manualmente.');
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const handleFamigliaChange = (famigliaId) => {
    const famiglia = famiglie.find(f => f.id === famigliaId);
    setSelectedFamiglia(famiglia);
    setCarrello([]);
    setSuggerimenti([]);
    setAiResult(null);
    if (famiglia) {
      fetchSuggestions(famigliaId, frequenza);
    }
  };

  const handleFrequenzaChange = (freq) => {
    setFrequenza(freq);
    if (selectedFamiglia) {
      fetchSuggestions(selectedFamiglia.id, freq);
    }
  };

  const addToCart = (prodotto) => {
    if (carrello.some(c => c.prodotto_id === prodotto.id)) {
      toast.error('Prodotto già nel carrello');
      return;
    }
    setCarrello([...carrello, {
      prodotto_id: prodotto.id,
      nome_prodotto: prodotto.nome_prodotto,
      quantita: 1,
      max_quantita: prodotto.quantita_disponibile
    }]);
  };

  const updateCartQuantity = (prodottoId, newQuantita) => {
    setCarrello(carrello.map(c => 
      c.prodotto_id === prodottoId 
        ? { ...c, quantita: Math.min(Math.max(0, newQuantita), c.max_quantita) }
        : c
    ));
  };

  const removeFromCart = (prodottoId) => {
    setCarrello(carrello.filter(c => c.prodotto_id !== prodottoId));
  };

  const getValoreTotale = () => {
    return carrello.reduce((sum, c) => {
      const prodotto = prodotti.find(p => p.id === c.prodotto_id);
      return sum + (prodotto?.valore_unitario || 0) * c.quantita;
    }, 0);
  };

  const handleSubmit = async () => {
    if (!selectedFamiglia) {
      toast.error('Seleziona una famiglia');
      return;
    }
    
    const validItems = carrello.filter(c => c.quantita > 0);
    if (validItems.length === 0) {
      toast.error('Aggiungi almeno un prodotto');
      return;
    }

    if (!firma) {
      toast.error('La firma di ritiro è obbligatoria');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/distribuzioni`, {
        famiglia_id: selectedFamiglia.id,
        prodotti: validItems.map(c => ({
          prodotto_id: c.prodotto_id,
          quantita: parseInt(c.quantita) || 0
        })),
        note: note || null,
        firma_ritiro: firma
      });
      toast.success('Distribuzione registrata con successo!');
      navigate('/distribuzioni');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Errore durante il salvataggio'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 animate-fade-in" data-testid="nuova-distribuzione-page">
      {/* Header */}
      <div className="flex items-center gap-2 sm:gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/distribuzioni')} className="h-10 w-10 flex-shrink-0" data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-lg sm:text-2xl font-heading font-bold">Nuova Distribuzione</h1>
          <p className="text-xs sm:text-sm text-muted-foreground">Registra con suggerimenti AI</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Main Form */}
        <div className="lg:col-span-2 space-y-4 sm:space-y-6">
          {/* Famiglia Selection */}
          <Card>
            <CardHeader className="px-4 pt-4 sm:px-6 sm:pt-6 pb-2">
              <CardTitle className="text-base">1. Seleziona Famiglia</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 sm:px-6 sm:pb-6">
              <Select value={selectedFamiglia?.id || ''} onValueChange={handleFamigliaChange}>
                <SelectTrigger data-testid="famiglia-select"><SelectValue placeholder="Seleziona una famiglia" /></SelectTrigger>
                <SelectContent>
                  {famiglie.map((f) => (
                    <SelectItem key={f.id} value={f.id}>{f.codice_famiglia} - {f.nome_referente} ({f.totale_componenti} pers.)</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {selectedFamiglia && (
                <div className="mt-3 space-y-3">
                  <div className="p-3 bg-muted rounded-lg">
                    <div className="grid grid-cols-2 gap-2 text-xs sm:text-sm">
                      <div><span className="text-muted-foreground">Referente:</span><p className="font-medium">{selectedFamiglia.nome_referente}</p></div>
                      <div><span className="text-muted-foreground">Componenti:</span><p className="font-medium">{selectedFamiglia.totale_componenti} persone</p></div>
                      <div>
                        <span className="text-muted-foreground">Priorita:</span>
                        <Badge variant={selectedFamiglia.priorita_sociale === 'alta' ? 'destructive' : 'secondary'} className="ml-1 text-xs">{selectedFamiglia.priorita_sociale}</Badge>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Ultima:</span>
                        <p className="font-medium">{selectedFamiglia.data_ultima_distribuzione ? new Date(selectedFamiglia.data_ultima_distribuzione).toLocaleDateString('it-IT') : 'Mai'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Frequenza */}
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">Frequenza distribuzione</Label>
                    <Select value={frequenza} onValueChange={handleFrequenzaChange}>
                      <SelectTrigger className="h-9" data-testid="frequenza-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="settimanale">Settimanale (7gg)</SelectItem>
                        <SelectItem value="ogni_10_giorni">Ogni 10 giorni</SelectItem>
                        <SelectItem value="ogni_15_giorni">Ogni 15 giorni</SelectItem>
                        <SelectItem value="mensile">Mensile (30gg)</SelectItem>
                        <SelectItem value="a_caduta">A caduta</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* AI Result Panel */}
                  {loadingSuggestions && (
                    <div className="flex items-center gap-2 p-3 bg-primary/5 rounded-lg">
                      <Loader2 className="w-4 h-4 animate-spin text-primary" />
                      <span className="text-sm text-primary">AI sta calcolando la distribuzione...</span>
                    </div>
                  )}
                  {aiResult && !loadingSuggestions && (
                    <div className="p-3 bg-primary/5 border border-primary/20 rounded-lg space-y-2">
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-primary" />
                        <span className="text-sm font-medium text-primary">Suggerimento AI</span>
                      </div>
                      <p className="text-xs text-muted-foreground">{aiResult.motivazione}</p>
                      {aiResult.riepilogo && (
                        <div className="flex flex-wrap gap-2 mt-1">
                          <Badge variant="outline" className="text-[10px]">{aiResult.riepilogo.componenti}</Badge>
                          <Badge variant="outline" className="text-[10px]">Copertura: {aiResult.riepilogo.copertura_giorni}gg</Badge>
                          <Badge variant="outline" className={`text-[10px] ${aiResult.riepilogo.stato_scorte === 'critico' ? 'text-red-600 border-red-600' : aiResult.riepilogo.stato_scorte === 'basso' ? 'text-amber-600 border-amber-600' : 'text-green-600 border-green-600'}`}>
                            Scorte: {aiResult.riepilogo.stato_scorte}
                          </Badge>
                          {aiResult.riepilogo.razionamento_attivo && (
                            <Badge variant="outline" className="text-[10px] text-red-600 border-red-600">Razionamento attivo</Badge>
                          )}
                        </div>
                      )}
                      {(aiResult.avvisi || []).length > 0 && (
                        <div className="mt-1">
                          {aiResult.avvisi.map((a, i) => (
                            <p key={i} className="text-[10px] text-amber-600">{a}</p>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Products */}
          <Card>
            <CardHeader className="px-4 pt-4 sm:px-6 sm:pt-6 pb-2">
              <CardTitle className="text-base">2. Seleziona Prodotti</CardTitle>
              <CardDescription className="text-xs sm:text-sm">
                {loadingSuggestions ? (
                  <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Caricamento suggerimenti AI...</span>
                ) : suggerimenti.length > 0 ? (
                  <span className="flex items-center gap-2 text-primary"><Sparkles className="w-4 h-4" />Suggerimenti AI applicati</span>
                ) : selectedFamiglia ? 'Aggiungi prodotti manualmente' : 'Seleziona prima una famiglia'}
              </CardDescription>
            </CardHeader>
            <CardContent className="px-4 pb-4 sm:px-6 sm:pb-6">
              {carrello.length > 0 ? (
                <div className="space-y-2">
                  {carrello.map((item) => (
                    <div key={item.prodotto_id} className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 bg-muted/50 rounded-lg">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{item.nome_prodotto}</p>
                        <p className="text-[10px] sm:text-xs text-muted-foreground">Max: {item.max_quantita}</p>
                      </div>
                      <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                        <Button variant="outline" size="icon" className="h-8 w-8 sm:h-9 sm:w-9" onClick={() => updateCartQuantity(item.prodotto_id, item.quantita - 1)}>
                          <Minus className="w-3 h-3 sm:w-4 sm:h-4" />
                        </Button>
                        <Input type="number" min="0" max={item.max_quantita} value={item.quantita}
                          onChange={(e) => updateCartQuantity(item.prodotto_id, parseInt(e.target.value) || 0)}
                          className="w-12 sm:w-16 text-center h-8 sm:h-9 text-sm" />
                        <Button variant="outline" size="icon" className="h-8 w-8 sm:h-9 sm:w-9" onClick={() => updateCartQuantity(item.prodotto_id, item.quantita + 1)}>
                          <Plus className="w-3 h-3 sm:w-4 sm:h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 sm:h-9 sm:w-9 text-destructive" onClick={() => removeFromCart(item.prodotto_id)}>
                          <X className="w-3 h-3 sm:w-4 sm:h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : selectedFamiglia && !loadingSuggestions ? (
                <div className="text-center py-6 text-muted-foreground">
                  <Package className="w-10 h-10 mx-auto opacity-50" />
                  <p className="mt-3 text-sm">Nessun prodotto nel carrello</p>
                </div>
              ) : null}

              {selectedFamiglia && (
                <div className="mt-3">
                  <Label className="text-xs text-muted-foreground">Aggiungi altro prodotto:</Label>
                  <Select onValueChange={(id) => addToCart(prodotti.find(p => p.id === id))}>
                    <SelectTrigger className="mt-1.5" data-testid="add-product-select"><SelectValue placeholder="Seleziona prodotto" /></SelectTrigger>
                    <SelectContent>
                      {prodotti.filter(p => !carrello.some(c => c.prodotto_id === p.id)).map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.nome_prodotto} (disp: {p.quantita_disponibile})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notes */}
          <Card>
            <CardHeader className="px-4 pt-4 sm:px-6 sm:pt-6 pb-2">
              <CardTitle className="text-base">3. Note (opzionale)</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 sm:px-6 sm:pb-6">
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Aggiungi eventuali note..." rows={3} data-testid="note-input" />
            </CardContent>
          </Card>

          {/* Firma di Ritiro */}
          {selectedFamiglia && carrello.filter(c => c.quantita > 0).length > 0 && (
            <Card>
              <CardHeader className="px-4 pt-4 sm:px-6 sm:pt-6 pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <PenLine className="w-4 h-4" /> 4. Firma di Ritiro
                </CardTitle>
                <CardDescription className="text-xs sm:text-sm">
                  Il beneficiario deve firmare per confermare il ritiro dei prodotti
                </CardDescription>
              </CardHeader>
              <CardContent className="px-4 pb-4 sm:px-6 sm:pb-6">
                {firma ? (
                  <div className="space-y-3" data-testid="firma-preview">
                    <div className="border rounded-lg p-2 bg-white">
                      <img src={firma} alt="Firma" className="w-full max-h-[120px] object-contain" />
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-green-600 border-green-600">Firmato</Badge>
                      <Button variant="outline" size="sm" onClick={() => setFirma(null)} data-testid="reset-firma-btn">
                        Rifai firma
                      </Button>
                    </div>
                  </div>
                ) : (
                  <SignaturePad
                    onSave={(dataUrl) => { setFirma(dataUrl); toast.success('Firma acquisita'); }}
                    onClear={() => setFirma(null)}
                    width={500}
                    height={180}
                  />
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Summary Sidebar / Mobile Bottom */}
        <div className="lg:col-span-1">
          <Card className="lg:sticky lg:top-24">
            <CardHeader className="px-4 pt-4 sm:px-6 sm:pt-6 pb-2">
              <CardTitle className="text-base">Riepilogo</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 sm:px-6 sm:pb-6 space-y-3">
              {selectedFamiglia ? (
                <>
                  <div className="grid grid-cols-2 lg:grid-cols-1 gap-2">
                    <div><p className="text-xs text-muted-foreground">Famiglia</p><p className="text-sm font-medium">{selectedFamiglia.nome_referente}</p></div>
                    <div><p className="text-xs text-muted-foreground">Prodotti</p><p className="text-sm font-medium">{carrello.filter(c => c.quantita > 0).length} selezionati</p></div>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Totale articoli</p>
                    <p className="text-xl sm:text-2xl font-heading font-bold">{carrello.reduce((sum, c) => sum + c.quantita, 0)}</p>
                  </div>
                  <div><p className="text-xs text-muted-foreground">Operatore</p><p className="text-sm font-medium">{user?.nome} {user?.cognome}</p></div>
                  <div><p className="text-xs text-muted-foreground">Firma</p>
                    {firma ? (
                      <Badge variant="outline" className="text-green-600 border-green-600">Firmato</Badge>
                    ) : (
                      <Badge variant="outline" className="text-amber-500 border-amber-500">In attesa</Badge>
                    )}
                  </div>
                </>
              ) : (
                <p className="text-muted-foreground text-center py-3 text-sm">Seleziona una famiglia per iniziare</p>
              )}

              <Button className="w-full" size="lg" onClick={handleSubmit}
                disabled={!selectedFamiglia || carrello.filter(c => c.quantita > 0).length === 0 || !firma || saving}
                data-testid="confirm-distribution-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                Conferma Distribuzione
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default DistribuzioniPage;

// Edit Distribution Page
export const ModificaDistribuzionePage = () => {
  const { id } = useParams();
  const { API, user } = useAuth();
  const navigate = useNavigate();
  const [distribuzione, setDistribuzione] = useState(null);
  const [famiglie, setFamiglie] = useState([]);
  const [prodotti, setProdotti] = useState([]);
  const [carrello, setCarrello] = useState([]);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, [id]);

  const fetchData = async () => {
    try {
      const [distRes, famRes, prodRes] = await Promise.all([
        axios.get(`${API}/distribuzioni`),
        axios.get(`${API}/famiglie`),
        axios.get(`${API}/prodotti`)
      ]);
      const dist = distRes.data.find(d => d.id === id);
      if (!dist) { toast.error('Distribuzione non trovata'); navigate('/distribuzioni'); return; }
      
      setDistribuzione(dist);
      setFamiglie(famRes.data);
      setProdotti(prodRes.data);
      setNote(dist.note || '');
      
      // Build cart from existing distribution
      const cart = dist.prodotti.map(p => {
        const prodotto = prodRes.data.find(pr => pr.id === p.prodotto_id);
        return {
          prodotto_id: p.prodotto_id,
          nome_prodotto: p.nome_prodotto,
          quantita: p.quantita,
          max_quantita: (prodotto?.quantita_disponibile || 0) + p.quantita,
          valore_unitario: prodotto?.valore_unitario || (p.valore / p.quantita)
        };
      });
      setCarrello(cart);
    } catch (err) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const updateCartQuantity = (prodotto_id, qty) => {
    setCarrello(prev => prev.map(item =>
      item.prodotto_id === prodotto_id
        ? { ...item, quantita: Math.max(0, Math.min(qty, item.max_quantita)) }
        : item
    ));
  };

  const removeFromCart = (prodotto_id) => {
    setCarrello(prev => prev.filter(item => item.prodotto_id !== prodotto_id));
  };

  const addToCart = (prodotto) => {
    if (!prodotto || carrello.some(c => c.prodotto_id === prodotto.id)) return;
    setCarrello(prev => [...prev, {
      prodotto_id: prodotto.id,
      nome_prodotto: prodotto.nome_prodotto,
      quantita: 1,
      max_quantita: prodotto.quantita_disponibile,
      valore_unitario: prodotto.valore_unitario
    }]);
  };

  const getValoreTotale = () => carrello.reduce((sum, item) => sum + (item.valore_unitario * item.quantita), 0);

  const handleSubmit = async () => {
    const prodottiValidi = carrello.filter(c => c.quantita > 0);
    if (prodottiValidi.length === 0) { toast.error('Aggiungi almeno un prodotto'); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/distribuzioni/${id}`, {
        famiglia_id: distribuzione.famiglia_id,
        prodotti: prodottiValidi.map(p => ({ prodotto_id: p.prodotto_id, quantita: p.quantita })),
        note
      });
      toast.success('Distribuzione aggiornata');
      navigate('/distribuzioni');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Errore'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-8 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto" /></div>;
  if (!distribuzione) return null;

  const famiglia = famiglie.find(f => f.id === distribuzione.famiglia_id);

  return (
    <div className="space-y-4 sm:space-y-6 animate-fade-in" data-testid="modifica-distribuzione-page">
      <div className="flex items-center gap-2 sm:gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/distribuzioni')} className="h-10 w-10 flex-shrink-0">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-lg sm:text-2xl font-heading font-bold">Modifica Distribuzione</h1>
          <p className="text-xs sm:text-sm text-muted-foreground">
            {famiglia?.nome_referente} - {new Date(distribuzione.data).toLocaleDateString('it-IT')}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <CardHeader className="px-4 pt-4 sm:px-6 sm:pt-6 pb-2">
              <CardTitle className="text-base">Prodotti</CardTitle>
              <CardDescription className="text-xs sm:text-sm">Modifica le quantita o rimuovi prodotti</CardDescription>
            </CardHeader>
            <CardContent className="px-4 pb-4 sm:px-6 sm:pb-6">
              {carrello.length > 0 ? (
                <div className="space-y-2">
                  {carrello.map((item) => (
                    <div key={item.prodotto_id} className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 bg-muted/50 rounded-lg">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{item.nome_prodotto}</p>
                        <p className="text-[10px] sm:text-xs text-muted-foreground">Max: {item.max_quantita}</p>
                      </div>
                      <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                        <Button variant="outline" size="icon" className="h-8 w-8 sm:h-9 sm:w-9" onClick={() => updateCartQuantity(item.prodotto_id, item.quantita - 1)}>
                          <Minus className="w-3 h-3 sm:w-4 sm:h-4" />
                        </Button>
                        <Input type="number" min="0" max={item.max_quantita} value={item.quantita}
                          onChange={(e) => updateCartQuantity(item.prodotto_id, parseInt(e.target.value) || 0)}
                          className="w-12 sm:w-16 text-center h-8 sm:h-9 text-sm" />
                        <Button variant="outline" size="icon" className="h-8 w-8 sm:h-9 sm:w-9" onClick={() => updateCartQuantity(item.prodotto_id, item.quantita + 1)}>
                          <Plus className="w-3 h-3 sm:w-4 sm:h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 sm:h-9 sm:w-9 text-destructive" onClick={() => removeFromCart(item.prodotto_id)}>
                          <X className="w-3 h-3 sm:w-4 sm:h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-muted-foreground">
                  <Package className="w-10 h-10 mx-auto opacity-50" />
                  <p className="mt-3 text-sm">Nessun prodotto</p>
                </div>
              )}
              <div className="mt-3">
                <Label className="text-xs text-muted-foreground">Aggiungi prodotto:</Label>
                <Select onValueChange={(pid) => addToCart(prodotti.find(p => p.id === pid))}>
                  <SelectTrigger className="mt-1.5"><SelectValue placeholder="Seleziona prodotto" /></SelectTrigger>
                  <SelectContent>
                    {prodotti.filter(p => !carrello.some(c => c.prodotto_id === p.id)).map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.nome_prodotto} (disp: {p.quantita_disponibile})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="px-4 pt-4 sm:px-6 sm:pt-6 pb-2">
              <CardTitle className="text-base">Note</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 sm:px-6 sm:pb-6">
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} />
            </CardContent>
          </Card>
        </div>

        <div className="lg:col-span-1">
          <Card className="lg:sticky lg:top-24">
            <CardHeader className="px-4 pt-4 sm:px-6 sm:pt-6 pb-2">
              <CardTitle className="text-base">Riepilogo</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 sm:px-6 sm:pb-6 space-y-3">
              <div className="grid grid-cols-2 lg:grid-cols-1 gap-2">
                <div><p className="text-xs text-muted-foreground">Famiglia</p><p className="text-sm font-medium">{famiglia?.nome_referente}</p></div>
                <div><p className="text-xs text-muted-foreground">Prodotti</p><p className="text-sm font-medium">{carrello.filter(c => c.quantita > 0).length} selezionati</p></div>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Totale articoli</p>
                <p className="text-xl sm:text-2xl font-heading font-bold">{carrello.reduce((sum, c) => sum + c.quantita, 0)}</p>
              </div>
              <Button className="w-full" size="lg" onClick={handleSubmit}
                disabled={carrello.filter(c => c.quantita > 0).length === 0 || saving}
                data-testid="save-distribution-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                Salva Modifiche
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
